import { type IBase } from "@/interfaces/api/base.interface"
import type { IFileBucket } from "@/interfaces/api/file-bucket.interface"
import { type IProject } from "@/interfaces/api/project.interface"
import type { IRentalDispense } from "@/interfaces/api/rental-dispense.interface"
import type { IRentalDispenseMaterialization } from "@/interfaces/api/rental-dispense-materialization.interface"
import type { ISale } from "@/interfaces/api/sale.interface"
import { type Moment } from "moment"
import type { IProductionStatementApprovalAllocation } from "@/interfaces/api/production-statement-approval-allocation.interface"
import type { IProductionStatementSnapshot } from "@/interfaces/api/production-statement-snapshot.interface"

export interface IProductionStatement extends IBase {
  project: IProject
  fileBucket?: IFileBucket
  periodStart?: Moment
  periodEnd?: Moment
  reference?: string
  disputedAt?: Moment | null
  comment?: string
  lastSentAt?: Moment | null
  lastRemindedAt?: Moment | null

  salesSum?: string
  rentalDispenseMaterializationsSum?: string

  saleIds?: string[]
  rentalDispenseIds?: string[]
  rentalDispenseMaterializationIds?: string[]

  sales: ISale[]
  rentalDispenseMaterializations: IRentalDispenseMaterialization[]
  rentalDispenses: IRentalDispense[]
  matchedAmountSum?: string
  productionStatementApprovalAllocations?: IProductionStatementApprovalAllocation[]
  /** Set when the statement was reverted (PNTS-3218). */
  deletedAt?: Moment | null
  deletedBy?: string | null
  /** The snapshot: nested when fully loaded, its id in a shallow list row, null for a legacy statement. */
  snapshot?: IProductionStatementSnapshot | string | null
  /** The snapshot's total, annotated on every row: a reverted statement's own rows are released. */
  snapshotTurnover?: string | null
}

/**
 * A preview returned by the drafts endpoint. It deliberately has no persistent
 * production-statement id: the backend creates one only when the draft is sent.
 */
export interface IDraftProductionStatement extends IProductionStatement {
  isComplete: boolean
  hasWarnings: boolean
}

export enum VirtualProductionStatementStatus {
  TO_CREATE = 1,
  SENT = 2,
  DISPUTED = 3,
  /** Reverted statements (PNTS-3218): read-only, shown with their snapshot. */
  DELETED = 4
}
